import RefreshIcon from "@mui/icons-material/Refresh";
import { Alert, AlertTitle, Button } from "@mui/material";

import { isCacheReadError } from "@/shared/errors";

import styles from "./RenderingError.module.css";

interface RenderingErrorProps {
  error: Error | null;
  title?: string;
  onClearError?: () => void;
}

export function RenderingError({
  error,
  title,
  onClearError,
}: RenderingErrorProps) {
  if (error === null) {
    return null;
  }

  const action = onClearError ? (
    <Button
      className={styles.reload}
      endIcon={<RefreshIcon fontSize="medium" />}
      onClick={onClearError}
      size="small"
    >
      Reload
    </Button>
  ) : null;

  // A cache read failure arrives as a parser complaint ("6 bytes remaining in
  // message index section") that names neither the cause nor the remedy. The reader
  // tries to delete the offending entry before raising, so a retry usually reads
  // freshly downloaded bytes; which control to press depends on whether Reload is
  // on screen.
  let defaultTitle = "Error rendering panel";
  let body = error.message;
  if (isCacheReadError(error)) {
    defaultTitle = "Cached data could not be read";
    body = action
      ? "Browser cache may be corrupted. Click Reload to refresh and try again."
      : "Browser cache may be corrupted. Reload the page to refresh and try again.";
  }

  return (
    <Alert action={action} className={styles.error} severity="error">
      <AlertTitle>{title ?? defaultTitle}</AlertTitle>
      {body}
    </Alert>
  );
}
